import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../../utils/api';
import { useSession } from '../../context/SessionContext';

const HistoryView = ({ sessionId, itemsVersion }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { participant, isConnected } = useSession();

  useEffect(() => {
    const fetchHistory = async () => {
      if (!sessionId || !participant || !isConnected) {
        if (!participant || !isConnected) {
            if (sessionId) setLoading(false);
        }
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/sessions/${sessionId}/history`);
        setHistory(response.data || []);
      } catch (err) {
        console.error('Error fetching session history:', err);
        setError(err.response?.data?.msg || err.message || 'Could not load session history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
  }, [sessionId, itemsVersion, participant, isConnected]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Paper sx={{ p: 3, my: 2 }}>
        <Typography variant="body1" align="center">
          No voting history available for this session yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table sx={{ minWidth: 650 }} size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Final Estimate</TableCell>
            <TableCell>Completed</TableCell>
            <TableCell>Participants</TableCell>
            <TableCell>Consensus</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell component="th" scope="row">
                <Typography variant="body2" fontWeight="medium">
                  {item.title}
                </Typography>
                {item.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {item.description.substring(0, 100)}
                    {item.description.length > 100 ? '...' : ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip 
                  label={item.finalEstimate || 'N/A'} 
                  color={item.finalEstimate ? 'primary' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {item.completedAt ? (
                  formatDistanceToNow(new Date(item.completedAt), { addSuffix: true })
                ) : (
                  'Not completed'
                )}
              </TableCell>
              <TableCell>
                {item.voters ? `${item.voters.length} voted` : 'N/A'}
              </TableCell>
              <TableCell>
                {item.consensus ? (
                  <Chip label="Yes" color="success" size="small" />
                ) : (
                  <Chip label="No" color="default" size="small" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HistoryView; 